import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import TuneIcon from '@material-ui/icons/Tune'
import CloseIcon from '@material-ui/icons/Close'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox'
import { Container, SortableLocationForm } from '../../../components'
import { useFinderSelection } from '../../../hooks'
import { Folder, Unit, UnitVisibility } from '../../../models'
import { Location } from '../../../store'

export interface CatalogItem {
    id: number
    name: string
}

export interface CatalogFolder {
    id: number
    name: string
    parent: number | null
    path: string
    path_ids: string
}

export interface CatalogUnit extends CatalogItem {
    folder: number
}

export interface CopyCatalog {
    statuses: CatalogItem[]
    status_groups: CatalogItem[]
    inventory_configs: CatalogItem[]
    area_configs: CatalogItem[]
    unit_configs: CatalogItem[]
    inventory_groups: CatalogItem[]
    inspection_types: CatalogItem[]
    damage_configs: CatalogItem[]
    folders: CatalogFolder[]
    units: CatalogUnit[]
}

export type CatalogKey = Exclude<keyof CopyCatalog, 'folders'>

export type CatalogSelection = Record<CatalogKey, Set<number>>

const CATEGORY_LABELS: Record<CatalogKey, string> = {
    statuses: 'Statuses',
    status_groups: 'Status Groups',
    inventory_configs: 'Inventory',
    area_configs: 'Areas',
    unit_configs: 'Unit Configs',
    inventory_groups: 'Inventory Groups',
    inspection_types: 'Inspection Types',
    damage_configs: 'Damage Configs',
    units: 'Units',
}

const CATEGORY_ORDER: Exclude<CatalogKey, 'units'>[] = [
    'statuses',
    'status_groups',
    'inventory_configs',
    'area_configs',
    'unit_configs',
    'inventory_groups',
    'inspection_types',
    'damage_configs',
]

const ALL_KEYS: CatalogKey[] = [...CATEGORY_ORDER, 'units']

const setsEqual = (left: Set<number>, right: Set<number>) => {
    if (left.size !== right.size) return false
    let equal = true
    left.forEach((id) => {
        if (!right.has(id)) equal = false
    })
    return equal
}

const stubFolder = (
    id: number,
    name: string,
    path = '/',
    pathIds = '/',
): Folder =>
    ({
        id,
        name,
        path,
        pathIds,
        parent: null,
        apartmentOwner: null,
        units: [],
        children: [],
        lineageUnitCount: 0,
    } as Folder)

const buildInfrastructure = (catalog: CopyCatalog): Folder => {
    const byId = new Map<number, Folder>()
    catalog.folders.forEach((folder) => {
        byId.set(
            folder.id,
            stubFolder(folder.id, folder.name, folder.path, folder.path_ids),
        )
    })

    const roots: Folder[] = []
    catalog.folders.forEach((folder) => {
        const node = byId.get(folder.id)
        if (!node) return
        const parent =
            folder.parent != null ? byId.get(folder.parent) : undefined
        if (parent) {
            node.parent = parent
            parent.children.push(node)
        } else {
            roots.push(node)
        }
    })

    catalog.units.forEach((unit) => {
        const folder = byId.get(unit.folder)
        if (!folder) return
        folder.units.push({
            id: unit.id,
            name: unit.name,
            unit_config: 0,
            folder,
            areas: [],
            workorders: [],
            inspectionDamages: [],
            inventoryInspections: [],
            visibility: UnitVisibility.ALWAYS_VISIBLE,
        } as Unit)
    })

    const root = stubFolder(-1, 'Property')
    root.children = roots
    return root
}

const collectUnits = (folder: Folder): Unit[] => {
    const units = [...folder.units]
    folder.children.forEach((child) => {
        units.push(...collectUnits(child))
    })
    return units
}

const countChipStyle = (
    allChecked: boolean,
    primary: string,
    grey: string,
    text: string,
): React.CSSProperties => ({
    fontWeight: 700,
    height: 22,
    backgroundColor: allChecked ? primary : grey,
    color: allChecked ? '#fff' : text,
})

interface ItemDialogProps {
    open: boolean
    title: string
    items: CatalogItem[]
    selected: Set<number>
    onClose: () => void
    onToggle: (id: number) => void
    onToggleAll: () => void
}

const ItemDialog: React.FC<ItemDialogProps> = ({
    open,
    title,
    items,
    selected,
    onClose,
    onToggle,
    onToggleAll,
}) => {
    const theme = useTheme()
    const [search, setSearch] = useState('')
    const allChecked = items.length > 0 && selected.size === items.length
    const someChecked = selected.size > 0 && !allChecked
    const filtered = items.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase()),
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ style: { borderRadius: 12 } }}
        >
            <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
                <Container style={{ alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                            {title}
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ color: theme.palette.text.secondary }}
                        >
                            {selected.size} of {items.length} selected
                        </Typography>
                    </div>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Container>
            </DialogTitle>
            <DialogContent dividers style={{ padding: 0 }}>
                <Container
                    style={{
                        alignItems: 'center',
                        padding: theme.spacing(1, 2),
                        borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                >
                    <Checkbox
                        checked={allChecked}
                        indeterminate={someChecked}
                        size="small"
                        color="primary"
                        onChange={onToggleAll}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        indeterminateIcon={
                            <IndeterminateCheckBoxIcon fontSize="small" />
                        }
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    />
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {allChecked ? 'Deselect all' : 'Select all'}
                    </Typography>
                    <div style={{ flex: 1 }} />
                    <SearchIcon
                        style={{
                            color: theme.palette.text.secondary,
                            fontSize: 18,
                            marginRight: 6,
                        }}
                    />
                    <TextField
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            disableUnderline: true,
                            style: { fontSize: 14 },
                        }}
                    />
                </Container>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        maxHeight: 420,
                        overflowY: 'auto',
                        padding: theme.spacing(1, 2),
                    }}
                >
                    {filtered.map((item) => (
                        <FormControlLabel
                            key={item.id}
                            control={
                                <Checkbox
                                    checked={selected.has(item.id)}
                                    size="small"
                                    color="primary"
                                    onChange={() => onToggle(item.id)}
                                />
                            }
                            label={
                                <Typography
                                    variant="body2"
                                    noWrap
                                    title={item.name}
                                >
                                    {item.name}
                                </Typography>
                            }
                            style={{
                                marginLeft: 0,
                                marginRight: 8,
                                minWidth: 0,
                            }}
                        />
                    ))}
                </div>
                {filtered.length === 0 && (
                    <Typography
                        variant="body2"
                        style={{
                            color: theme.palette.text.secondary,
                            padding: theme.spacing(3),
                            textAlign: 'center',
                        }}
                    >
                        No matches
                    </Typography>
                )}
            </DialogContent>
            <DialogActions style={{ padding: theme.spacing(1.5, 2) }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="primary"
                    style={{ borderRadius: 8, textTransform: 'none' }}
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface CopyItemPickerProps {
    catalog: CopyCatalog | null
    catalogLoading: boolean
    selection: CatalogSelection
    onSelectionChange: (selection: CatalogSelection) => void
}

export const CopyItemPicker: React.FC<CopyItemPickerProps> = ({
    catalog,
    catalogLoading,
    selection,
    onSelectionChange,
}) => {
    const theme = useTheme()
    const [openKey, setOpenKey] = useState<CatalogKey | null>(null)
    const {
        finderSelection,
        selectLocation,
        setSelection,
    } = useFinderSelection()
    const userToggledUnits = useRef(false)
    const selectionRef = useRef(selection)
    selectionRef.current = selection

    const infrastructure = useMemo(
        () => (catalog ? buildInfrastructure(catalog) : null),
        [catalog],
    )

    useEffect(() => {
        userToggledUnits.current = false
        if (!infrastructure) return
        setSelection(collectUnits(infrastructure))
    }, [infrastructure, setSelection])

    useEffect(() => {
        if (!catalog) return
        const ids = new Set<number>()
        Object.keys(finderSelection.unit).forEach((key) => {
            if (key === 'length') return
            const id = Number(key)
            if (finderSelection.unit[id]) ids.add(id)
        })
        if (
            ids.size === 0 &&
            catalog.units.length > 0 &&
            !userToggledUnits.current
        ) {
            return
        }
        if (setsEqual(selectionRef.current.units, ids)) return
        onSelectionChange({ ...selectionRef.current, units: ids })
    }, [finderSelection, catalog, onSelectionChange])

    const handleToggle = (key: CatalogKey, id: number) => {
        const next = new Set(selection[key])
        if (next.has(id)) next.delete(id)
        else next.add(id)
        onSelectionChange({ ...selection, [key]: next })
    }

    const handleToggleAll = (key: CatalogKey) => {
        if (!catalog) return
        const items = catalog[key]
        const next =
            selection[key].size === items.length
                ? new Set<number>()
                : new Set(items.map((item) => item.id))
        onSelectionChange({ ...selection, [key]: next })
    }

    const handleUnitSelect = (location: Location) => {
        userToggledUnits.current = true
        selectLocation(location)
    }

    if (!catalog && !catalogLoading) return null

    const visibleKeys = ALL_KEYS.filter(
        (key) => catalog && catalog[key].length > 0,
    )
    const selectedCount = catalog
        ? ALL_KEYS.reduce((sum, key) => sum + selection[key].size, 0)
        : 0
    const totalCount = catalog
        ? ALL_KEYS.reduce((sum, key) => sum + catalog[key].length, 0)
        : 0
    const openItems =
        catalog && openKey && openKey !== 'units' ? catalog[openKey] : []

    return (
        <Paper
            variant="outlined"
            style={{
                borderRadius: 10,
                backgroundColor: '#fff',
                overflow: 'hidden',
                margin: theme.spacing(0, 3, 2),
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(1.5, 2),
                    backgroundColor: theme.palette.grey[100],
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                    alignItems: 'center',
                }}
            >
                <TuneIcon
                    style={{
                        color: theme.palette.text.secondary,
                        fontSize: 18,
                        marginRight: theme.spacing(1),
                    }}
                />
                <div style={{ flex: 1 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        Items to Copy
                    </Typography>
                    <Typography
                        variant="caption"
                        style={{ color: theme.palette.text.secondary }}
                    >
                        Everything starts selected. Uncheck anything you want to
                        leave behind.
                    </Typography>
                </div>
                {catalog && (
                    <Chip
                        label={`${selectedCount} of ${totalCount}`}
                        size="small"
                        style={{
                            fontWeight: 700,
                            backgroundColor: theme.palette.primary.main,
                            color: '#fff',
                        }}
                    />
                )}
            </Container>

            {catalogLoading && (
                <Container
                    style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: theme.spacing(4),
                    }}
                >
                    <CircularProgress size={22} />
                    <Typography
                        variant="body2"
                        style={{
                            color: theme.palette.text.secondary,
                            marginLeft: theme.spacing(1.5),
                        }}
                    >
                        Loading items from the source apartment
                    </Typography>
                </Container>
            )}

            {catalog && !catalogLoading && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns:
                            'repeat(auto-fill, minmax(220px, 1fr))',
                        gap: theme.spacing(1.5),
                        padding: theme.spacing(2),
                    }}
                >
                    {visibleKeys.map((key) => {
                        const total = catalog[key].length
                        const count = selection[key].size
                        const allChecked = count === total
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setOpenKey(key)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                    borderRadius: 10,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    backgroundColor: '#fff',
                                    padding: theme.spacing(1.5),
                                    outline: 'none',
                                    transition:
                                        'border-color 0.15s, box-shadow 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor =
                                        theme.palette.primary.main
                                    e.currentTarget.style.boxShadow =
                                        '0 2px 8px rgba(0,0,0,0.06)'
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor =
                                        theme.palette.grey[300]
                                    e.currentTarget.style.boxShadow = 'none'
                                }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 700 }}
                                    >
                                        {CATEGORY_LABELS[key]}
                                    </Typography>
                                    <Chip
                                        label={
                                            allChecked
                                                ? `All ${total}`
                                                : `${count} of ${total}`
                                        }
                                        size="small"
                                        style={{
                                            ...countChipStyle(
                                                allChecked,
                                                theme.palette.primary.main,
                                                theme.palette.grey[200],
                                                theme.palette.text.primary,
                                            ),
                                            marginTop: 6,
                                        }}
                                    />
                                </div>
                                <ChevronRightIcon
                                    style={{
                                        color: theme.palette.text.secondary,
                                    }}
                                />
                            </button>
                        )
                    })}
                </div>
            )}

            {catalog && openKey && openKey !== 'units' && (
                <ItemDialog
                    open
                    title={CATEGORY_LABELS[openKey]}
                    items={openItems}
                    selected={selection[openKey]}
                    onClose={() => setOpenKey(null)}
                    onToggle={(id) => handleToggle(openKey, id)}
                    onToggleAll={() => handleToggleAll(openKey)}
                />
            )}

            {catalog && infrastructure && (
                <Dialog
                    open={openKey === 'units'}
                    onClose={() => setOpenKey(null)}
                    fullWidth
                    maxWidth="md"
                    PaperProps={{ style: { borderRadius: 12 } }}
                >
                    <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
                        <Container style={{ alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <Typography
                                    variant="h6"
                                    style={{ fontWeight: 700 }}
                                >
                                    Units
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {selection.units.size} of{' '}
                                    {catalog.units.length} selected
                                </Typography>
                            </div>
                            <IconButton
                                size="small"
                                onClick={() => setOpenKey(null)}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Container>
                    </DialogTitle>
                    <DialogContent dividers style={{ padding: 0 }}>
                        <SortableLocationForm
                            root={infrastructure}
                            locationSelection={finderSelection}
                            setLocationSelection={handleUnitSelect}
                            useCheckbox
                            defaultOpenFirstLevel
                            maxHeight={480}
                            style={{ padding: theme.spacing(1, 2) }}
                        />
                    </DialogContent>
                    <DialogActions style={{ padding: theme.spacing(1.5, 2) }}>
                        <Button
                            onClick={() => setOpenKey(null)}
                            variant="contained"
                            color="primary"
                            style={{ borderRadius: 8, textTransform: 'none' }}
                        >
                            Done
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Paper>
    )
}

export const buildFullSelection = (catalog: CopyCatalog): CatalogSelection => {
    const selection = {} as CatalogSelection
    ALL_KEYS.forEach((key) => {
        selection[key] = new Set(catalog[key].map((item) => item.id))
    })
    return selection
}

export const isAllSelected = (
    catalog: CopyCatalog,
    selection: CatalogSelection,
    key: CatalogKey,
): boolean => selection[key].size === catalog[key].length

export const getSelectionIds = (
    catalog: CopyCatalog,
    selection: CatalogSelection,
    key: CatalogKey,
): number[] | undefined => {
    if (isAllSelected(catalog, selection, key)) return undefined
    return Array.from(selection[key])
}
